/**
 * Stripe.js publishable key — same account/mode as buyer iOS/Android apps and server STRIPE_SECRET_KEY.
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { BUYDROP_STRIPE_PUBLISHABLE_KEY } from '../config/buydropStripePublishableKey';
import { getWebApiBaseUrl } from '../config/apiBaseUrl';

let runtimeKeyPromise: Promise<{ key: string; mode: string | null }> | null = null;
let stripePromise: Promise<Stripe | null> | null = null;
let lastKeyUsed: string | null = null;

function modeFromKey(key: string): string | null {
  if (key.startsWith('pk_test_')) return 'test';
  if (key.startsWith('pk_live_')) return 'live';
  return null;
}

export function getStripePublishableKeyFromBuild(): string {
  return (process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '').trim();
}

async function fetchStripeConfigFromApi(): Promise<{ key: string; mode: string | null }> {
  if (typeof window === 'undefined') return { key: '', mode: null };
  if (!runtimeKeyPromise) {
    runtimeKeyPromise = (async () => {
      try {
        const base = getWebApiBaseUrl().replace(/\/$/, '');
        const res = await fetch(`${base}/config`, { credentials: 'same-origin' });
        if (!res.ok) return { key: '', mode: null };
        const data = await res.json();
        return {
          key: (data.stripePublishableKey || '').trim(),
          mode: data.stripeMode || null,
        };
      } catch {
        return { key: '', mode: null };
      }
    })();
  }
  return runtimeKeyPromise;
}

export async function resolveStripePublishableKey(): Promise<{
  key: string;
  mode: string | null;
}> {
  const fromBuild = getStripePublishableKeyFromBuild();
  if (fromBuild.startsWith('pk_')) {
    return { key: fromBuild, mode: modeFromKey(fromBuild) };
  }

  const fromApi = await fetchStripeConfigFromApi();
  if (fromApi.key.startsWith('pk_')) {
    return fromApi;
  }

  if (BUYDROP_STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
    return { key: BUYDROP_STRIPE_PUBLISHABLE_KEY, mode: modeFromKey(BUYDROP_STRIPE_PUBLISHABLE_KEY) };
  }

  return { key: '', mode: null };
}

export async function initStripeCheckout(): Promise<{ stripe: Stripe | null; mode: string | null }> {
  const { key, mode } = await resolveStripePublishableKey();
  if (!key) {
    console.warn('[Checkout] Stripe publishable key missing — check server/.env and client/.env');
    return { stripe: null, mode };
  }
  if (lastKeyUsed !== null && lastKeyUsed !== key) {
    stripePromise = null;
  }
  lastKeyUsed = key;
  if (!stripePromise) {
    stripePromise = loadStripe(key);
  }
  const stripe = await stripePromise;
  return { stripe, mode };
}

export function loadStripeClient(): Promise<Stripe | null> {
  return initStripeCheckout().then(({ stripe }) => stripe);
}
